import { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { 
  Database, Search, TrendingUp, RefreshCw, Zap,
  Brain, Target, AlertTriangle, CheckCircle, Loader2,
  BarChart3, PieChart, Activity, Sparkles
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../../App';

const VectorAnalyticsDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [indexing, setIndexing] = useState(false);
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [ragContext, setRagContext] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/vector-search/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch vector stats:', error);
      toast.error('Failed to load vector analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleIndexPlans = async () => {
    setIndexing(true);
    try {
      await axios.post(`${API}/vector-search/index-plans`, { index_all: true });
      toast.success('Indexing started in background');
      // Refresh stats after a delay
      setTimeout(fetchStats, 3000);
    } catch (error) {
      toast.error('Failed to start indexing');
    } finally {
      setIndexing(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const response = await axios.post(`${API}/vector-search/search-similar-plans`, {
        query: searchQuery,
        limit: 5,
        min_score: 0.1
      });
      setSearchResults(response.data.results || []);
      
      if (response.data.results?.length === 0) {
        toast.info('No similar plans found');
      }
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const handleGetRagContext = async () => {
    try {
      const response = await axios.get(`${API}/vector-search/rag-context?specialty=Healthcare`);
      setRagContext(response.data);
    } catch (error) {
      toast.error('Failed to get RAG context');
    }
  };

  const formatSimilarity = (score) => {
    return `${(score * 100).toFixed(1)}%`;
  };

  const getSimilarityColor = (score) => {
    if (score >= 0.7) return 'text-green-600 bg-green-100';
    if (score >= 0.4) return 'text-yellow-600 bg-yellow-100';
    return 'text-orange-600 bg-orange-100';
  };

  return (
    <div className="space-y-6" data-testid="vector-analytics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg">
            <Database className="h-6 w-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Vector Analytics</h2>
            <p className="text-sm text-gray-500">RAG System Performance & Embeddings</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={fetchStats}
            disabled={loading}
            data-testid="refresh-stats-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            onClick={handleIndexPlans}
            disabled={indexing}
            className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700"
            data-testid="index-plans-btn"
          >
            {indexing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Zap className="h-4 w-4 mr-2" />
            )}
            Index All Plans
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-violet-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Plan Embeddings</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {stats?.plan_embeddings || 0}
                </p>
              </div>
              <div className="p-3 bg-violet-100 rounded-full">
                <BarChart3 className="h-6 w-6 text-violet-600" />
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Indexed membership plans
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Member Behaviors</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {stats?.member_behavior_embeddings || 0}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <Activity className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Indexed for churn prediction
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">RAG Status</p>
                <p className="text-xl font-bold text-green-600">
                  {stats?.plan_embeddings > 0 ? 'Active' : 'No Data'}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Brain className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              AI suggestions use vector context
            </p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Specialties</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white">
                  {stats?.specialty_distribution?.length || 0}
                </p>
              </div>
              <div className="p-3 bg-orange-100 rounded-full">
                <PieChart className="h-6 w-6 text-orange-600" />
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Unique specialties indexed
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Specialty Distribution */}
      {stats?.specialty_distribution?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5 text-violet-600" />
              Specialty Distribution
            </CardTitle>
            <CardDescription>
              Distribution of indexed plans by clinic specialty
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.specialty_distribution.map((item, index) => (
                <div key={index} className="flex items-center gap-4">
                  <span className="w-32 text-sm font-medium truncate">
                    {item._id || 'Not specified'}
                  </span>
                  <div className="flex-1">
                    <Progress 
                      value={(item.count / stats.plan_embeddings) * 100} 
                      className="h-2"
                    />
                  </div>
                  <Badge variant="outline" className="min-w-[60px] justify-center">
                    {item.count} plans
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Semantic Search Test */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5 text-blue-600" />
            Semantic Search Test
          </CardTitle>
          <CardDescription>
            Test the vector search by finding similar membership plans
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="flex gap-2 mb-4">
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="e.g., 'premium healthcare membership with unlimited visits'"
              className="flex-1"
              data-testid="search-query-input"
            />
            <Button type="submit" disabled={searching} data-testid="search-btn">
              {searching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
            </Button>
          </form>

          {searchResults.length > 0 && (
            <div className="space-y-3">
              <p className="text-sm font-medium text-gray-700">
                Found {searchResults.length} similar plans:
              </p>
              {searchResults.map((result, index) => (
                <div 
                  key={index}
                  className="p-4 border rounded-lg bg-gray-50 dark:bg-gray-800"
                  data-testid={`search-result-${index}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">
                        {result.plan_data?.name || 'Unknown Plan'}
                      </h4>
                      <p className="text-sm text-gray-500">
                        ${result.plan_data?.price || 0}/month
                      </p>
                    </div>
                    <Badge className={getSimilarityColor(result.similarity_score)}>
                      {formatSimilarity(result.similarity_score)} match
                    </Badge>
                  </div>
                  {result.plan_data?.benefits?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {result.plan_data.benefits.slice(0, 3).map((benefit, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {benefit}
                        </Badge>
                      ))}
                    </div>
                  )}
                  <p className="text-xs text-gray-400 mt-2 truncate">
                    {result.text_content}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* RAG Context Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-purple-600" />
            RAG Context Preview
          </CardTitle>
          <CardDescription>
            Preview the context that AI suggestions receive from similar plans
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            variant="outline" 
            onClick={handleGetRagContext}
            className="mb-4"
            data-testid="get-rag-context-btn"
          >
            <Brain className="h-4 w-4 mr-2" />
            Generate RAG Context
          </Button>
          
          {ragContext && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Badge variant={ragContext.has_context ? 'default' : 'secondary'}>
                  {ragContext.has_context ? (
                    <><CheckCircle className="h-3 w-3 mr-1" /> Context Available</>
                  ) : (
                    <><AlertTriangle className="h-3 w-3 mr-1" /> No Context</>
                  )}
                </Badge>
                <span className="text-sm text-gray-500">
                  Specialty: {ragContext.specialty}
                </span>
              </div>
              
              {ragContext.context && (
                <pre className="p-4 bg-gray-900 text-green-400 rounded-lg text-xs overflow-auto max-h-64 font-mono">
                  {ragContext.context}
                </pre>
              )}
            </div>
          )}
        </CardContent>
        <CardFooter className="text-xs text-gray-500">
          This context is automatically included when generating AI membership suggestions
        </CardFooter>
      </Card>

      {/* How It Works */}
      <Card className="bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 border-violet-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-violet-700 dark:text-violet-300">
            <Target className="h-5 w-5" />
            How Vector Analytics Works
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="p-4 bg-white dark:bg-gray-800 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-violet-100 rounded">
                  <Database className="h-4 w-4 text-violet-600" />
                </div>
                <span className="font-semibold text-sm">1. Indexing</span>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Membership plans are converted into 1536-dimensional vectors that capture semantic meaning.
              </p>
            </div>
            
            <div className="p-4 bg-white dark:bg-gray-800 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-blue-100 rounded">
                  <Search className="h-4 w-4 text-blue-600" />
                </div>
                <span className="font-semibold text-sm">2. Similarity Search</span>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Cosine similarity finds plans with similar features, pricing, and benefits.
              </p>
            </div>
            
            <div className="p-4 bg-white dark:bg-gray-800 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 bg-green-100 rounded">
                  <Sparkles className="h-4 w-4 text-green-600" />
                </div>
                <span className="font-semibold text-sm">3. RAG Enhancement</span>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                AI suggestions use similar plans as context for more relevant recommendations.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VectorAnalyticsDashboard;
